import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Chip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as CustomersIcon,
  Receipt as InvoicesIcon,
  AccountBalance as BankingIcon,
  Assessment as ReportsIcon,
  CreditCard as ExpensesIcon,
  Hub as IntegrationIcon,
  Business as BusinessRegistryIcon,
  Timeline as MonitoringIcon,
  Flag as USFlagIcon,
  Groups as EmployeesIcon,
  Edit as TransactionIcon,
  AccountTree as FinanceIcon,
  Calculate as SalesTaxIcon,
  SmartToy as AIIcon,
  Psychology as AgentIcon,
  ContactPhone as CRMIcon,
  Inventory as SupplyChainIcon,
  WorkOutline as ProjectManagementIcon,
  AutoAwesome as ExecutiveAIIcon,
  OpenInNew as ExternalLinkIcon,
  Security as SecurityIcon,
  Gavel as ComplianceIcon,
  History as AuditIcon,
  Notifications as AlertsIcon,
  Backup as BackupIcon,
  DeleteSweep as DataRetentionIcon,
  AdminPanelSettings as UsersIcon,
  PeopleAlt as ReferralsIcon
} from '@mui/icons-material';

const DRAWER_WIDTH = 280;

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  path: string;
  badge?: string;
  comingSoon?: boolean;
  external?: boolean;
  externalUrl?: string;
}

const navigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard'
  },
  {
    id: 'ai-chat',
    label: 'AI Chat',
    icon: <AIIcon />,
    path: '/ai-chat',
    badge: 'AI'
  },
  // 'AI Agents' is now the main Dashboard
  // {
  //   id: 'ai-agents',
  //   label: 'AI Agents',
  //   icon: <AgentIcon />,
  //   path: '/ai-agents',
  //   badge: 'NEW'
  // },
  {
    id: 'employees',
    label: 'Employee Management',
    icon: <EmployeesIcon />,
    path: '/employees',
    badge: 'ALL'
  },
  {
    id: 'finance',
    label: 'Chart of Accounts',
    icon: <FinanceIcon />,
    path: '/finance',
    badge: 'NEW'
  },
  {
    id: 'transactions',
    label: 'Transaction Entry',
    icon: <TransactionIcon />,
    path: '/transactions',
    badge: 'NEW'
  },
  {
    id: 'reports',
    label: 'Financial Reports',
    icon: <ReportsIcon />,
    path: '/reports',
    badge: 'NEW'
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: <CustomersIcon />,
    path: '/customers'
  },
  {
    id: 'invoices',
    label: 'Invoices',
    icon: <InvoicesIcon />,
    path: '/invoices'
  },
  {
    id: 'expenses',
    label: 'Expenses',
    icon: <ExpensesIcon />,
    path: '/expenses'
  },
  {
    id: 'sales-tax',
    label: 'Sales Tax Returns',
    icon: <SalesTaxIcon />,
    path: '/vat',
    badge: 'E-File'
  },
  {
    id: 'irs',
    label: 'IRS Integration',
    icon: <USFlagIcon />,
    path: '/irs',
    badge: 'E-File'
  },
  {
    id: 'irs-pay',
    label: 'Pay IRS',
    icon: <BankingIcon />,
    path: '/irs/pay',
    badge: 'NEW'
  },
  {
    id: 'banking',
    label: 'Banking',
    icon: <BankingIcon />,
    path: '/banking',
    badge: 'Open Banking'
  },
  {
    id: 'advanced-reports',
    label: 'Advanced Reports',
    icon: <ReportsIcon />,
    path: '/advanced-reports',
    badge: 'NEW'
  },
  {
    id: 'business-registry',
    label: 'Secretary of State',
    icon: <BusinessRegistryIcon />,
    path: '/business-registry',
    badge: 'NEW'
  },
  {
    id: 'integration',
    label: 'Integration',
    icon: <IntegrationIcon />,
    path: '/integration',
    badge: 'Cross-Domain'
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    icon: <MonitoringIcon />,
    path: '/monitoring',
    badge: 'NEW'
  },
  // Security Dashboard hidden from end users - access via direct URL /security for admins
  // {
  //   id: 'security',
  //   label: 'Security',
  //   icon: <SecurityIcon />,
  //   path: '/security',
  //   badge: 'Enterprise'
  // },
  {
    id: 'compliance',
    label: 'Compliance',
    icon: <ComplianceIcon />,
    path: '/compliance',
    badge: 'US'
  },
  {
    id: 'audit',
    label: 'Audit Log',
    icon: <AuditIcon />,
    path: '/audit',
    badge: 'Enterprise'
  },
  {
    id: 'alerts',
    label: 'Alerts',
    icon: <AlertsIcon />,
    path: '/alerts',
    badge: 'BI'
  },
  {
    id: 'backup',
    label: 'Backup & Recovery',
    icon: <BackupIcon />,
    path: '/backup',
    badge: 'Enterprise'
  },
  {
    id: 'data-retention',
    label: 'Data Retention',
    icon: <DataRetentionIcon />,
    path: '/data-retention',
    badge: 'CCPA'
  },
  {
    id: 'users',
    label: 'User Management',
    icon: <UsersIcon />,
    path: '/users',
    badge: 'RBAC'
  },
  {
    id: 'referrals',
    label: 'Referrals',
    icon: <ReferralsIcon />,
    path: '/referrals',
    badge: 'Earn'
  }
];

// Executive Suite Apps - External Links
const executiveSuiteApps: NavigationItem[] = [
  {
    id: 'executive-ai',
    label: 'Executive Force AI',
    icon: <ExecutiveAIIcon />,
    path: '#',
    external: true,
    externalUrl: 'http://localhost:3003',
    badge: 'AI Suite'
  },
  {
    id: 'crm',
    label: 'Sentient CRM',
    icon: <CRMIcon />,
    path: '#',
    external: true,
    externalUrl: 'http://localhost:3001',
    badge: 'CRM'
  },
  {
    id: 'ascin',
    label: 'ASCIN Supply Chain',
    icon: <SupplyChainIcon />,
    path: '#',
    external: true,
    externalUrl: 'http://localhost:3004',
    badge: 'Supply'
  },
  {
    id: 'project-mgmt',
    label: 'Project Management',
    icon: <ProjectManagementIcon />,
    path: '#',
    external: true,
    externalUrl: 'http://localhost:3005',
    badge: 'PM'
  }
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path: string, external?: boolean, externalUrl?: string) => {
    if (external && externalUrl) {
      window.open(externalUrl, '_blank');
    } else {
      navigate(path);
    }
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.drawer,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          <USFlagIcon sx={{ color: '#1976d2' }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Executive Finance
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Modern Financial Management for US Businesses
        </Typography>
        <Chip
          label="US Optimized"
          size="small"
          color="secondary"
          sx={{ mt: 1 }}
        />
      </Box>

      <Divider />

      {/* Navigation */}
      <List sx={{ px: 2, py: 1 }}>
        {navigationItems.map((item) => (
          <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleNavigation(item.path, item.external, item.externalUrl)}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: location.pathname === item.path ? 'white' : 'text.secondary',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: location.pathname === item.path ? 600 : 400,
                }}
              />
              {item.badge && (
                <Chip
                  label={item.badge}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.7rem',
                    height: 20,
                    color: location.pathname === item.path ? 'white' : 'primary.main',
                    borderColor: location.pathname === item.path ? 'white' : 'primary.main',
                  }}
                />
              )}
              {item.comingSoon && (
                <Chip
                  label="Soon"
                  size="small"
                  color="warning"
                  sx={{
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ mt: 2 }} />

      {/* Executive Suite Apps - Now handled by BusinessSuiteNavigation in header */}
      {/* Keeping this section commented for reference
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ px: 1 }}>
          Executive Suite
        </Typography>
        <List sx={{ py: 0 }}>
          {executiveSuiteApps.map((app) => (
            <ListItem key={app.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigation(app.path, app.external, app.externalUrl)}
                sx={{
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: 'text.secondary',
                  }}
                >
                  {app.icon}
                </ListItemIcon>
                <ListItemText
                  primary={app.label}
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: 400,
                  }}
                />
                {app.badge && (
                  <Chip
                    label={app.badge}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.65rem',
                      height: 18,
                      color: 'secondary.main',
                      borderColor: 'secondary.main',
                    }}
                  />
                )}
                {app.external && (
                  <ExternalLinkIcon sx={{ fontSize: 16, ml: 0.5, color: 'text.secondary' }} />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      */}

      {/* Divider removed since Executive Suite section is commented out */}

      {/* Help & Support Section */}
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Help & Support
        </Typography>
        <List sx={{ py: 0 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => navigate('/user-guide')}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemText
                primary="User Guide"
                primaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => navigate('/faq')}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemText
                primary="FAQs"
                primaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => navigate('/privacy-policy')}
              sx={{ borderRadius: 1 }}
            >
              <ListItemText
                primary="Privacy Policy"
                primaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      <Divider />

      {/* US Features Highlight */}
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          US Features
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Chip label="IRS E-File Ready" size="small" color="success" variant="outlined" />
          <Chip label="Secretary of State" size="small" color="info" variant="outlined" />
          <Chip label="US Sales Tax Compliant" size="small" color="primary" variant="outlined" />
          <Chip label="Open Banking" size="small" color="secondary" variant="outlined" />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Designed specifically for US businesses with full compliance and modern features.
        </Typography>
      </Box>

      {/* Competitive Advantage */}
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', mt: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          🚀 Competitive Advantage
        </Typography>
        <Typography variant="caption">
          Cross-domain integration with CRM and Supply Chain for unified business intelligence.
        </Typography>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
